import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getBeginningOfNextDay, offDutyStatuses, onDutyStatuses, type IState } from "../helpers";
import { type TComputedAvailabilityResult } from "./helpers/shared";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";

const findNextWindowShiftDate = (
    state: IState,
    cycleDays: number,
    nextMidnightDate: Date,
    hasDuty: (dayIndex: number) => boolean,
): Date | undefined => {
    const N = state.days.length;
    const C = state.cycle.days;
    let midnightDate = nextMidnightDate;
    for (let k = 0; k < cycleDays; k++) {
        if (k > 0) {
            midnightDate = getBeginningOfNextDay(midnightDate, state.toLocalTime, state.toUtcTime);
        }
        if (C + k + 1 < cycleDays) continue;
        const i = N - cycleDays + k;
        if (i < 0) continue;
        if (i < N - state.cycle.days) continue;
        if (hasDuty(i)) {
            return midnightDate;
        }
    }
    return undefined;
};

export type CycleDutyConditions = {
    avail: number | undefined;
    isConsuming: boolean;
    hasDutyInCurrentCycle: boolean;
    cycleResetIsNoOp: boolean;
};

export class CycleDutyCalculator extends AvailabilityCalculator {
    private conditions(state: IState): CycleDutyConditions {
        const avail = discreteAvailabilityFunctions.Cycle(state, false);
        const isConsuming = onDutyStatuses.indexOf(state.current?.status) !== -1;
        const cycleDays = state.config.cycle?.cycleDays;
        const cycleDaysInWindow = (cycleDays !== undefined && state.cycle.days >= cycleDays) ? cycleDays : state.cycle.days;
        const hasDutyInCurrentCycle = state.days.slice(-cycleDaysInWindow).some(d => (d.onDutyCount ?? 0) > 0);
        const configCycleMaximum = state.config.cycle?.cycleMaximum ?? 0;
        const effectiveCycleMaximum = state.cycle.cycleMaximum || configCycleMaximum;
        const cycleResetIsNoOp = effectiveCycleMaximum - state.cycle.onDutyCount === configCycleMaximum;
        return { avail, isConsuming, hasDutyInCurrentCycle, cycleResetIsNoOp };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isConsuming;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;
        if (c.isConsuming && c.avail > 0) {
            return new Date(state.currentDate.getTime() + c.avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        const factors: Record<string, TAvailabilityFactorEntry> = {};

        const nextDayBoundaryDate = this.nextDayBoundary(state);
        const cycleDays = state.config.cycle?.cycleDays;
        const isNonRolling = state.config.cycle?.nonRolling === true;

        const cycleResetDate = this.cycleResetDate(state);

        const cycleWindowShiftDate: Date | undefined = (() => {
            if (!cycleDays || isNonRolling) return undefined;
            const d = findNextWindowShiftDate(state, cycleDays, nextDayBoundaryDate, i => (state.days[i]?.onDutyCount ?? 0) > 0);
            if (d === undefined) return undefined;
            if (cycleResetDate !== undefined && d.getTime() >= cycleResetDate.getTime()) return undefined;
            return d;
        })();

        if (cycleWindowShiftDate) {
            factors.cycleWindowShift = { factor: () => cycleWindowShiftDate, label: "cycle window shift" };
        }

        const cycleResetDateActual = (c.hasDutyInCurrentCycle && !c.cycleResetIsNoOp) ? cycleResetDate : undefined;
        if (cycleResetDateActual) {
            factors.cycleReset = { factor: () => cycleResetDateActual, label: "cycle reset" };
        }

        return factors;
    }
}

export type CycleDrivingConditions = {
    avail: number | undefined;
    isConsuming: boolean;
    isOffDuty: boolean;
    hasDrivingInCurrentCycle: boolean;
    hasCyclePercentConstraint: boolean;
};

export class CycleDrivingCalculator extends AvailabilityCalculator {
    private conditions(state: IState): CycleDrivingConditions {
        const avail = discreteAvailabilityFunctions.CycleDriving(state, false);
        const isConsuming = state.current?.status === "D";
        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const cycleDays = state.config.cycle?.cycleDays;
        const cycleDaysInWindow = (cycleDays !== undefined && state.cycle.days >= cycleDays) ? cycleDays : state.cycle.days;
        const hasDrivingInCurrentCycle = state.days.slice(-cycleDaysInWindow).some(d => (d.driveCount ?? 0) > 0);
        const hasCyclePercentConstraint = !!(state.config.cycle?.cycleDays && state.config.cycle?.maxPercentDriving);
        return { avail, isConsuming, isOffDuty, hasDrivingInCurrentCycle, hasCyclePercentConstraint };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isConsuming;
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;
        if (c.isConsuming && c.avail > 0) {
            return new Date(state.currentDate.getTime() + c.avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        const factors: Record<string, TAvailabilityFactorEntry> = {};

        const nextDayBoundaryDate = this.nextDayBoundary(state);
        const cycleDays = state.config.cycle?.cycleDays;
        const isNonRolling = state.config.cycle?.nonRolling === true;

        const cycleResetDate = this.cycleResetDate(state);

        const cycleWindowShiftDate: Date | undefined = (() => {
            if (!cycleDays || isNonRolling) return undefined;
            const d = findNextWindowShiftDate(state, cycleDays, nextDayBoundaryDate, i => (state.days[i]?.driveCount ?? 0) > 0);
            if (d === undefined) return undefined;
            if (cycleResetDate !== undefined && d.getTime() >= cycleResetDate.getTime()) return undefined;
            return d;
        })();

        if (cycleWindowShiftDate) {
            factors.cycleWindowShift = { factor: () => cycleWindowShiftDate, label: "cycle window shift" };
        }

        const cycleResetDateActual = c.hasDrivingInCurrentCycle ? cycleResetDate : undefined;
        if (cycleResetDateActual) {
            factors.cycleReset = { factor: () => cycleResetDateActual, label: "cycle reset" };
        }

        const cyclePercentCrossingDate: Date | undefined = (() => {
            if (!c.isOffDuty || !c.hasCyclePercentConstraint) return undefined;
            const cycleDrivingMaximum = state.config.cycle?.cycleDrivingMaximum;
            if (!cycleDrivingMaximum) return undefined;

            const ONE_DAY_MS = 24 * 60 * 60 * 1000;
            const fixedPart = (cycleDays! - state.cycle.days) * ONE_DAY_MS + state.cycle.onDutyCount;
            const crossingMs = nextDayBoundaryDate.getTime() + fixedPart - 2 * cycleDrivingMaximum;

            if (crossingMs <= state.currentDate.getTime()) {
                return new Date(state.currentDate.getTime());
            }
            if (cycleResetDate !== undefined && crossingMs >= cycleResetDate.getTime()) return undefined;

            return new Date(crossingMs);
        })();
        
        if (cyclePercentCrossingDate) {
            factors.cyclePercentCrossing = { factor: () => cyclePercentCrossingDate, label: "cycle percent crossing" };
        }

        return factors;
    }
}

export const extractCycleDutyConditions = (state: IState): CycleDutyConditions =>
    new CycleDutyCalculator().extractConditions(state) as CycleDutyConditions;

export const computeCycleDutyResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new CycleDutyCalculator().compute(state);

export const extractCycleDrivingConditions = (state: IState): CycleDrivingConditions =>
    new CycleDrivingCalculator().extractConditions(state) as CycleDrivingConditions;

export const computeCycleDrivingResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new CycleDrivingCalculator().compute(state);
